import { useEffect, useState } from 'react';
import axios from 'axios';
import { Navigate, Link } from 'react-router-dom';
import {
  Loader2, Flag, Check, X, RefreshCw, ExternalLink, Trash2,
  ShieldCheck, Download, ArrowRight,
} from 'lucide-react';
import { API, useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { devError } from '../utils/logger';
import { toast } from 'sonner';

const ADMIN_EMAILS = ['thisisradas@gmail.com'];

const REASON_COLOURS = {
  copyright: 'bg-rose-500/15 text-rose-300 border-rose-500/30',
  misleading_or_scam: 'bg-amber-500/15 text-amber-300 border-amber-500/30',
  duplicate: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
  off_topic: 'bg-slate-500/15 text-slate-300 border-slate-500/30',
  wrong_photos: 'bg-blue-500/15 text-blue-300 border-blue-500/30',
  sold_or_unavailable: 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30',
  other: 'bg-violet-500/15 text-violet-300 border-violet-500/30',
};

/**
 * Admin triage queue at /admin/reports.
 *
 * Shows pending listing reports with one-click "Resolve" or "Take down listing"
 * actions. Take-down flips the listing's availability to `removed` (soft delete
 * — the row stays for audit).
 */
export default function AdminReportsPage() {
  const { user, loading: authLoading } = useAuth();
  const isAdmin = !!user && (user.user_type === 'admin' || (user.email && ADMIN_EMAILS.includes(user.email.toLowerCase())));

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [acting, setActing] = useState(null);
  const [notes, setNotes] = useState({});

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/reports?status=pending`);
      setItems(data.items || []);
    } catch (e) {
      devError('admin-reports', e);
      toast.error(e.response?.data?.detail || 'Failed to load reports');
    } finally { setLoading(false); }
  };

  useEffect(() => { if (isAdmin) load(); }, [isAdmin]);

  if (authLoading) {
    return <div className="min-h-screen pt-24 flex items-center justify-center"><Loader2 className="w-6 h-6 text-accent animate-spin" /></div>;
  }
  if (!isAdmin) return <Navigate to="/" replace />;

  const resolve = async (reportId, takeDown) => {
    setActing(reportId);
    try {
      await axios.post(`${API}/admin/reports/${reportId}/resolve`, {
        take_down_listing: takeDown,
        note: notes[reportId] || null,
      });
      toast.success(takeDown ? 'Listing taken down + report resolved' : 'Report resolved');
      setItems((p) => p.filter((r) => r.report_id !== reportId));
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action failed');
    } finally { setActing(null); }
  };

  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="admin-reports-page">
      <div className="max-w-5xl mx-auto px-6 md:px-12">
        <div className="mb-8 flex items-end justify-between gap-4 flex-wrap">
          <div>
            <p className="text-xs uppercase tracking-[0.2em] text-accent mb-2 inline-flex items-center gap-2">
              <Flag className="w-3 h-3" /> Reported listings
            </p>
            <h1 className="font-playfair text-3xl md:text-4xl font-bold mb-2">
              Triage queue
            </h1>
            <p className="text-sm text-muted-foreground">
              {loading ? 'Loading…' : `${items.length} pending report${items.length === 1 ? '' : 's'}`}
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <Link to="/admin/inspirations">
              <Button variant="outline" data-testid="admin-inspirations-link">
                <ArrowRight className="w-4 h-4 mr-2" />
                Inspirations
              </Button>
            </Link>
            <Link to="/admin/suppliers">
              <Button variant="outline">
                <ShieldCheck className="w-4 h-4 mr-2" />
                Supplier queue
              </Button>
            </Link>
            <Link to="/admin/imports">
              <Button variant="outline">
                <Download className="w-4 h-4 mr-2" />
                Imports
              </Button>
            </Link>
            <Button variant="outline" onClick={load} disabled={loading} data-testid="admin-reports-refresh">
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-accent animate-spin" />
          </div>
        ) : items.length === 0 ? (
          <div className="text-center py-20 rounded-xl border border-border/20 bg-card/30">
            <Check className="w-10 h-10 text-emerald-400/60 mx-auto mb-3" />
            <p className="text-muted-foreground">All clear — no pending reports.</p>
          </div>
        ) : (
          <div className="space-y-4" data-testid="admin-reports-list">
            {items.map((r) => (
              <article
                key={r.report_id}
                className="rounded-xl border border-border/30 bg-card/30 p-5"
                data-testid={`report-${r.report_id}`}
              >
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="min-w-0">
                    <Link
                      to={`/listing/${r.listing_id}`}
                      target="_blank"
                      rel="noreferrer"
                      className="font-playfair text-lg font-semibold text-foreground hover:text-accent inline-flex items-center gap-1"
                    >
                      {r.listing_title || r.listing_id}
                      <ExternalLink className="w-3.5 h-3.5" />
                    </Link>
                    <p className="text-xs text-muted-foreground mt-0.5">
                      {r.listing_external_source ? `External (${r.listing_external_source})` : 'Native listing'}
                      {' • '}{new Date(r.created_at).toLocaleString()}
                    </p>
                  </div>
                  <span className={`text-[10px] uppercase tracking-wider px-2 py-1 rounded border ${REASON_COLOURS[r.reason] || REASON_COLOURS.other}`}>
                    {r.reason_label || r.reason}
                  </span>
                </div>

                {r.message && (
                  <p className="text-sm text-foreground/85 mt-3 whitespace-pre-wrap rounded-md bg-background/40 border border-border/20 p-3">
                    {r.message}
                  </p>
                )}

                <p className="text-xs text-muted-foreground mt-3">
                  Reporter: {r.reporter_email || (r.reporter_user_id ? `user ${r.reporter_user_id.slice(0, 12)}…` : 'anonymous')}
                </p>

                <div className="mt-4">
                  <label className="text-xs uppercase tracking-wider text-muted-foreground mb-1 block">
                    Resolution note (optional, kept for audit)
                  </label>
                  <Textarea
                    rows={2}
                    placeholder="e.g. 'Photos swapped — original removed at supplier's request'"
                    value={notes[r.report_id] || ''}
                    onChange={(e) => setNotes((s) => ({ ...s, [r.report_id]: e.target.value }))}
                    className="text-sm bg-background/40"
                    data-testid={`report-note-${r.report_id}`}
                  />
                </div>

                <div className="flex items-center justify-end gap-2 mt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => resolve(r.report_id, false)}
                    disabled={acting === r.report_id}
                    data-testid={`report-dismiss-${r.report_id}`}
                  >
                    <Check className="w-4 h-4 mr-2" />
                    Resolve (no action)
                  </Button>
                  <Button
                    size="sm"
                    onClick={() => resolve(r.report_id, true)}
                    disabled={acting === r.report_id}
                    className="bg-rose-600 hover:bg-rose-500 text-white"
                    data-testid={`report-takedown-${r.report_id}`}
                  >
                    {acting === r.report_id ? (
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Trash2 className="w-4 h-4 mr-2" />
                    )}
                    Take down listing
                  </Button>
                </div>
              </article>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
